/**
 * FreezeModal — conferma congelamento snapshot Business Review
 *
 * Flusso:
 *   1. click "Salva snapshot" in BusinessReview toolbar
 *   2. questo modal mostra period_label + avviso immutabilità
 *   3. click "Congela" → onConfirm() → chiamata API create+freeze
 */
import { Snowflake, X } from 'lucide-react'

interface FreezeModalProps {
  periodLabel: string
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

export function FreezeModal({
  periodLabel,
  onConfirm,
  onCancel,
  isLoading = false,
}: FreezeModalProps) {
  return (
    <div
      data-testid="freeze-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div
        data-testid="freeze-modal"
        className="relative w-full max-w-md rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] p-6 shadow-[var(--km-shadow-lg)]"
      >
        {/* Close */}
        <button
          type="button"
          data-testid="freeze-modal-close"
          onClick={onCancel}
          className="absolute top-4 right-4 p-1 rounded-lg text-[--km-text-secondary] hover:text-[--km-text-primary] hover:bg-white/5 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-xl bg-indigo-500/10 p-2.5">
            <Snowflake className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[--km-text-primary]">
              Congela review
            </h2>
            <p className="text-xs text-[--km-text-secondary]">Snapshot immutabile</p>
          </div>
        </div>

        {/* Body */}
        <p className="text-sm text-[--km-text-primary] mb-2">
          Congelare la review{' '}
          <span className="font-semibold text-indigo-400" data-testid="freeze-modal-period">
            {periodLabel}
          </span>
          ?
        </p>
        <p className="text-xs text-[--km-text-secondary] mb-6">
          I valori KPI verranno fotografati al momento attuale e non potranno essere modificati.
          Lo snapshot apparirà nello storico della Business Review.
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            data-testid="freeze-modal-cancel"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-xl border border-[var(--km-card-border)] text-sm text-[--km-text-secondary] hover:text-[--km-text-primary] hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            data-testid="freeze-modal-confirm"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading
              ? <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              : <Snowflake className="w-3.5 h-3.5" />
            }
            Congela
          </button>
        </div>
      </div>
    </div>
  )
}
